import type {EcommerceSalesChannel} from '@/types/salesChannels';
import type {EventSocketMessage, SalesChannelsChangedEventValue} from '@myparcel-frontend/event-service';
import type {NotificationVariant} from '@myparcel-frontend/notifications';
import type {ValueOf} from '@myparcel-frontend/types';
import type {ComposerTranslation} from 'vue-i18n';
import {matchEventServiceEvent} from '@myparcel-frontend/event-service';
import {useNotification} from '@myparcel-frontend/notifications';
import {camelCase} from 'es-toolkit/string';
import {useSalesChannelStore} from '@/store/salesChannelStore/salesChannelStore';
import {SalesChannelsStatus} from '@/types/salesChannels';

export default function useEventServiceEvents() {
  const {addNotification} = useNotification();

  const {
    updateSalesChannelToDelegationIssued,
    updateSalesChannelToDelegationRequested,
    updateSalesChannelToDelegationRequired,
  } = useSalesChannelStore();

  const sendNotification = (salesChannel: EcommerceSalesChannel, t: ComposerTranslation): void => {
    let variant: ValueOf<typeof NotificationVariant> = 'success';

    const body = t(`integrations.salesChannels.notifications.statusChanged.${camelCase(salesChannel.status)}`, {
      name: salesChannel.name,
    });

    if (salesChannel.status === SalesChannelsStatus.DelegationRequired) {
      variant = 'alert';
    }

    addNotification({
      variant,
      body,
    });
  };

  const handleSalesChannelsChangedEvent = (data: SalesChannelsChangedEventValue, t: ComposerTranslation): void => {
    let updatedSalesChannel: EcommerceSalesChannel | undefined;

    const {salesChannelId, status} = data;

    switch (status.case) {
      case SalesChannelsStatus.DelegationIssued:
        updatedSalesChannel = updateSalesChannelToDelegationIssued(salesChannelId, status.value);

        if (!updatedSalesChannel) {
          return;
        }

        sendNotification(updatedSalesChannel, t);
        break;
      case SalesChannelsStatus.DelegationRequested:
        updatedSalesChannel = updateSalesChannelToDelegationRequested(salesChannelId, status.value);
        break;
      case SalesChannelsStatus.DelegationRequired:
        updatedSalesChannel = updateSalesChannelToDelegationRequired(salesChannelId, status.value);

        if (!updatedSalesChannel) {
          return;
        }

        sendNotification(updatedSalesChannel, t);
        break;
      default:
        break;
    }
  };

  const handleEventMessage = (message: EventSocketMessage, t: ComposerTranslation) =>
    matchEventServiceEvent(message, {
      onSalesChannelStatusChanged: (value) => handleSalesChannelsChangedEvent(value, t),
    });

  return {
    handleEventMessage,
  };
}
